"use client";

import React, { useLayoutEffect, useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import _merge from "lodash/merge";

import { BoxProps } from "../Box";
import Flex from "../Flex";
import factory from "../../testUtils/factory";
import useBoolean from "../../hooks/useBoolean";
import Text from "../Text";
import TooltipTail from "./TooltipTail";
import { TooltipDirection, TooltipPosition } from "./TooltipTypes";

import {
  ElementPositioningProps,
  ElementBorderProps,
  ElementTransitionProps,
} from "../ComponentProps";
import useIsClient from "../../hooks/useIsClient";

type Props = {
  source: string;
  direction?: TooltipDirection;
  children: React.ReactNode;
  interactive?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
} & ElementPositioningProps &
  ElementBorderProps &
  ElementTransitionProps;

type WrapperProps = {
  tooltipPositioning: TooltipPosition;
  isOpen: boolean;
} & BoxProps;

const Wrapper = styled(Flex).attrs<WrapperProps>(props => {
  return {
    background: "modalOverlay",
    boxShadow: "panel",
    maxWidth: props.maxWidth || 360,
    minWidth: props.minWidth || 50,
    flexDirection: "column",
    position: "fixed",
    display: "block",
    borderRadius: "default",
    zIndex: props.zIndex || "tooltip",
    style: {
      lineHeight: "30px",
      transition: "opacity 0.1s",
      opacity: props.isOpen ? 1 : 0,
      pointerEvents: props.isOpen ? null : "none",
      transitionDelay: props.isOpen ? props.transitionDelay : "0s", // only delay if opening
      ...props.tooltipPositioning,
    },
  };
})<WrapperProps>``;

// Helper function for getting DOM elements
function getElement(name: string, value: string): HTMLElement {
  let el;
  if (typeof value === "string") {
    try {
      el = document.querySelector(value);
    } catch {
      // Retry another selector below
    }
    if (!el) el = document.querySelector(`[${name}='${value}']`);
  }
  return el;
}

/**
 * A Tooltip component
 *
 * ### Example usage
 * ```
 * const tooltipID = "my-tooltip"
 * <>
 *  <Text data-tooltip-source={tooltipId}>I have a tooltip</Text>
 *  <Tooltip source={tooltipId} direction={}>
 *    You found the tooltip!
 *  </Tooltip>
 * </>
 * ```
 */

const Tooltip: React.FC<Props> = ({
  source,
  direction = "right",
  children,
  interactive = true,
  onVisibilityChange,
  ...props
}) => {
  const [isOpen, setOpen, setClosed] = useBoolean(false);
  const [wantsOpen, setWantsOpen, setWantsClosed] = useBoolean(false);
  const [position, setPosition] = useState<TooltipPosition>({});
  const contentIdentifier = useMemo(() => factory.genString(), []);

  useEffect(() => {
    onVisibilityChange?.(isOpen);
  }, [isOpen, onVisibilityChange]);

  // MARK : Elemenets
  // -------------------------------------------------------------------------------
  const getContentElement = useCallback(
    () => document.body.querySelector(`#${contentIdentifier}`) as HTMLElement,
    [contentIdentifier]
  );

  const getSource = useCallback((): HTMLElement => {
    return getElement("data-tooltip-source", source);
  }, [source]);

  const getTrigger = useCallback((): HTMLElement => {
    return getElement("data-tooltip-trigger", source) || getSource();
  }, [getSource, source]);

  // Set accessability attributes on the source
  // This obviously isn't very JSX-like but it allows for a more flexibility
  // when composing tooltips with components since we don't have to pass around
  // refs or props. This way it is all handled implicitly.
  useEffect(() => {
    getSource()?.setAttribute("aria-describedby", contentIdentifier);
  }, [contentIdentifier, getSource]);

  // MARK : Positioning
  // -------------------------------------------------------------------------------
  const calculatePosition = useCallback(() => {
    const sourceEl = getSource();
    const contentEl = getContentElement();
    if (!sourceEl || !contentEl) return;

    const rect = sourceEl.getBoundingClientRect(),
      windowRect = document.body.getBoundingClientRect(),
      contentRect = contentEl.getBoundingClientRect();

    const pos: TooltipPosition = {
      tail: {
        direction: TooltipDirection.Left as TooltipDirection,
      },
    };

    const defaultPad = 8;
    const tailSize = 8;
    function positionCenterY() {
      const sourceCenter = rect.top + rect.height / 2;

      pos.top = sourceCenter - contentRect.height / 2;
      pos.top = Math.max(defaultPad, pos.top);

      let contentCenter = pos.top + contentRect.height / 2;

      const estimatedTop = pos.top + contentRect.height;
      if (estimatedTop > windowRect.height) {
        pos.bottom = 8;
        pos.top = null;
        contentCenter = windowRect.height - 8 - contentRect.height / 2;
      }
      if (contentCenter !== sourceCenter) {
        pos.tail.offset = sourceCenter - contentCenter;
      }
    }

    function positionCenterX() {
      const sourceCenter = rect.left + rect.width / 2;
      pos.left = sourceCenter - contentRect.width / 2;
      pos.left = Math.max(defaultPad, pos.left);

      let contentCenter = pos.left + contentRect.width / 2;

      const estimatedRight = pos.left + contentRect.width;
      if (estimatedRight > windowRect.width) {
        pos.right = 8;
        pos.left = null;
        contentCenter = windowRect.width - 8 - contentRect.width / 2;
      }

      if (contentCenter !== sourceCenter) {
        pos.tail.offset = sourceCenter - contentCenter;
      }
    }

    switch (direction) {
      case TooltipDirection.Left:
        positionCenterY();
        if (rect.left - contentRect.width < 8) {
          pos.left = rect.left + tailSize;
          pos.tail.direction = TooltipDirection.Left;
        } else {
          pos.right = windowRect.width - rect.left + tailSize;
          pos.tail.direction = TooltipDirection.Right;
        }
        break;
      case TooltipDirection.Right: {
        const maxX = rect.left + rect.width;
        positionCenterY();

        if (maxX > windowRect.width - tailSize) {
          // If the source is past the endge
          pos.right = defaultPad + tailSize;
          pos.tail.direction = TooltipDirection.Right;
        } else if (maxX + contentRect.width > windowRect.width) {
          // If the source is inset but too close
          pos.right = windowRect.width - rect.right + tailSize;
          pos.tail.direction = TooltipDirection.Right;
        } else {
          pos.left = maxX + tailSize;
          pos.tail.direction = TooltipDirection.Left;
        }
        break;
      }
      case TooltipDirection.Top: {
        const desiredBottom = windowRect.height - rect.top;
        positionCenterX();
        if (rect.top < 0) {
          pos.top = rect.bottom + tailSize;
          pos.tail.direction = TooltipDirection.Top;
        } else if (desiredBottom + contentRect.height > windowRect.height) {
          pos.top = rect.bottom + tailSize;
          pos.tail.direction = TooltipDirection.Top;
        } else {
          pos.bottom = desiredBottom + tailSize;
          pos.tail.direction = TooltipDirection.Bottom;
        }
        break;
      }
      case TooltipDirection.Bottom:
        {
          const desiredTop = rect.bottom;
          positionCenterX();
          if (rect.bottom > windowRect.height) {
            pos.bottom = windowRect.height - rect.top;
            pos.tail.direction = TooltipDirection.Bottom;
          } else if (desiredTop + contentRect.height > windowRect.height) {
            pos.bottom = windowRect.height - rect.bottom;
            pos.tail.direction = TooltipDirection.Bottom;
          } else {
            pos.top = desiredTop;
            pos.tail.direction = TooltipDirection.Top;
          }
        }
        break;
      default:
        break;
    }
    setPosition(pos);
  }, [direction, getContentElement, getSource]);

  useLayoutEffect(() => {
    calculatePosition();
  }, [isOpen, direction, children, calculatePosition]);

  useEffect(() => {
    if (wantsOpen && !isOpen) setOpen();
    else if (!wantsOpen && isOpen) {
      const timeout = setTimeout(setClosed, 150);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, setClosed, setOpen, wantsOpen]);

  // MARK : Managing Open State
  // -------------------------------------------------------------------------------

  useEffect(() => {
    const triggerEl = getTrigger();
    if (!triggerEl) return;

    const openEvents = ["focus", "mouseenter"];
    const closeEvents = ["blur", "mouseleave"];

    const elements = [triggerEl];
    const contentEl = getContentElement();
    if (contentEl && interactive) elements.push(contentEl);

    openEvents.forEach(e => elements.forEach(el => el.addEventListener(e, setWantsOpen)));
    closeEvents.forEach(e => elements.forEach(el => el.addEventListener(e, setWantsClosed)));

    return () => {
      openEvents.forEach(e => elements.forEach(el => el.removeEventListener(e, setWantsOpen)));
      closeEvents.forEach(e => elements.forEach(el => el.removeEventListener(e, setWantsClosed)));
    };
  }, [getContentElement, getTrigger, wantsOpen, setWantsClosed, setWantsOpen, interactive]);

  useEffect(() => {
    if (isOpen) window.addEventListener("scroll", calculatePosition, true);
    return () => {
      window.removeEventListener("scroll", calculatePosition, true);
    };
  }, [calculatePosition, isOpen]);

  const isClient = useIsClient();
  if (!isClient) return null;

  // MARK : Rendering
  // -------------------------------------------------------------------------------
  function renderContent() {
    return (
      <Text as="span" font="ui.small" color="white">
        {children}
      </Text>
    );
  }

  function renderTooltip() {
    const { tail, ...style } = position;
    return (
      // Use style instead of passing the position props via Box/Flex
      // to avoid styled components generating too many classes
      <Wrapper
        id={contentIdentifier}
        data-testid="tooltip-wrapper"
        tooltipPositioning={style}
        onClick={setClosed}
        {...props}
        isOpen={isOpen}
        role="tooltip">
        <TooltipTail {...tail} />
        <Flex
          flexDirection="column"
          alignItems="center"
          maxHeight="368"
          overflow="auto"
          data-testid="tooltip-content"
          p="s">
          {renderContent()}
        </Flex>
      </Wrapper>
    );
  }

  return createPortal(renderTooltip(), document.body);
};

export default Tooltip;
